/**
 * Entity creation feature options for centralized gating.
 *
 * This module is inspired by client/processes/processes-platform/FeatureOptions.tsx. Ideally, the use cases
 * would use a plugin pattern, but without that, we use this centralized gating module.
 */
import BenchlingConfig from 'BenchlingConfig';

import {EntityCreationFeatureType, CreatableEntityType} from './EntityCreationConfigTypes';

type FeatureConfigDict = {
  // Use boolean for static values and a function for BenchlingConfig flags.
  [feature in EntityCreationFeatureType]: boolean | (() => boolean);
};

const DEFAULT__ALL_FEATURES_DISABLED: FeatureConfigDict = {
  REGISTRATION_TABLE: false,
  ADD_FROM_SPREADSHEET_TO_REGISTRATION_TABLE: false,
  LEGACY_REGISTRATION_TABLE: false,
  IMPORT_FROM_SPREADSHEET: false,
};
const ALL_FEATURES_ENABLED: FeatureConfigDict = {
  REGISTRATION_TABLE: true,
  ADD_FROM_SPREADSHEET_TO_REGISTRATION_TABLE: true,
  LEGACY_REGISTRATION_TABLE: true,
  IMPORT_FROM_SPREADSHEET: true,
};

const ENTITY_CREATION_TYPE_TO_FEATURE_CONFIGS: {
  [itemType in CreatableEntityType]?: FeatureConfigDict;
} = {
  // Entry
  //
  // Not an entity, so it does not support entity creation!
  ENTRY: DEFAULT__ALL_FEATURES_DISABLED,
  // Custom Entity and simple SSI-backed entities
  //
  // The latter are entities backed by Basic Folder Item + exclusive Static Schema Interface that only has
  // schema fields, no system fields.
  BASIC_FOLDER_ITEM: ALL_FEATURES_ENABLED,
  EQUIPMENT: ALL_FEATURES_ENABLED,
  // Nucleotide Sequences and Oligos
  DNA_OLIGO: ALL_FEATURES_ENABLED,
  DNA_SEQUENCE: ALL_FEATURES_ENABLED,
  RNA_OLIGO: ALL_FEATURES_ENABLED,
  RNA_SEQUENCE: ALL_FEATURES_ENABLED,
  MIXED: ALL_FEATURES_ENABLED,
  // AA Sequence
  AA_SEQUENCE: ALL_FEATURES_ENABLED,
  // Mixture
  MIXTURE: ALL_FEATURES_ENABLED,
  // Molecule
  MOLECULE: ALL_FEATURES_ENABLED,
  // Complex Polymers
  OLIGO_CONJUGATE: {
    ...DEFAULT__ALL_FEATURES_DISABLED,
    REGISTRATION_TABLE: true,
    // This is false because it doesn't support the COMPLEX_POLYMER_COMPONENT columns.
    // See https://benchling.atlassian.net/wiki/spaces/MBMB/pages/1329661323/Add+From+Spreadsheet+-+Current+State
    // for more information.
    ADD_FROM_SPREADSHEET_TO_REGISTRATION_TABLE: false,
    IMPORT_FROM_SPREADSHEET: true,
  },
  OLIGO_DUPLEX: {
    ...DEFAULT__ALL_FEATURES_DISABLED,
    REGISTRATION_TABLE: true,
    // This is false because it doesn't support the COMPLEX_POLYMER_COMPONENT columns.
    // See https://benchling.atlassian.net/wiki/spaces/MBMB/pages/1329661323/Add+From+Spreadsheet+-+Current+State
    // for more information.
    ADD_FROM_SPREADSHEET_TO_REGISTRATION_TABLE: false,
    IMPORT_FROM_SPREADSHEET: true,
  },
  // Antibody-Drug Conjugate
  //
  // Registration tables are backed by mock ADC data, so they also require the mock flag.
  ADC: {
    ...DEFAULT__ALL_FEATURES_DISABLED,
    REGISTRATION_TABLE: () => BenchlingConfig.MOLBIO_ENABLE_ADCS && BenchlingConfig.MOLBIO_ENABLE_MOCK_ADCS,
  },
  // Antibody
  ANTIBODY: {
    ...DEFAULT__ALL_FEATURES_DISABLED,
    REGISTRATION_TABLE: () => BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_REGISTRATION_TABLE,
    // This is false because it doesn't support the ANTIBODY_DOMAIN columns.
    // See https://benchling.atlassian.net/wiki/spaces/MBMB/pages/1329661323/Add+From+Spreadsheet+-+Current+State
    // for more information.
    ADD_FROM_SPREADSHEET_TO_REGISTRATION_TABLE: false,
    IMPORT_FROM_SPREADSHEET: false,
  },
  // Trial Sample
  //
  // It forces its own special entity creation flow.
  TRIAL_SAMPLE: DEFAULT__ALL_FEATURES_DISABLED,

  // By default, map your new entity type to `DEFAULT__ALL_FEATURES_DISABLED`. Then, as you implement entity
  // creation features, update the values to true (or a config flag) to officially enable the feature.
  //
  // See https://benchling.atlassian.net/wiki/spaces/IR/pages/1284768966/How-To+Add+New+Entity+Type+to+Creation+Code+Paths
  // for more information on how to add new entity types to the entity creation code paths.
  //
  // * REGISTRATION_TABLE - See "Implement Registration Table" section.
  // * ADD_FROM_SPREADSHEET_TO_REGISTRATION_TABLE - See "Implement Registration Table" section.
  // * LEGACY_REGISTRATION_TABLE - This is a legacy feature that is not required for new entity types.
};

/**
 * Determines whether a specific entity creation feature is enabled for the given entity type.
 *
 * @param entityType The type of entity being created. The best way to get this value is to call
 *                   tagSchemaToCreatableEntityType on your tagSchema.
 * @param feature The entity creation feature to check
 * @returns Whether the feature is enabled for this entity type
 */
export function isEntityCreationFeatureEnabled(
  entityType: CreatableEntityType,
  feature: EntityCreationFeatureType
): boolean {
  const featureConfig = ENTITY_CREATION_TYPE_TO_FEATURE_CONFIGS[entityType] ?? DEFAULT__ALL_FEATURES_DISABLED;
  const featureValue = featureConfig[feature];
  return typeof featureValue === 'boolean' ? featureValue : featureValue();
}
